import { Text } from "dash-styled"
import { getFileName, TableColumn } from "resha"

import { APIJobModel, JobStatus } from "../../../../../../Models/API/JobModel"
import { MultipleValueFilterOperator } from "../../../../../../Models/Client/FilterExpression"
import { getValue, hasValue } from "../../../../../../Models/Client/FetchState"
import { FlatButton } from "../../../../../Common/FlatButton/FlatButton"
import { NoData } from "../../../../../Common/NoData/NoData"
import { LabelWithHint } from "../../../../../Common/LabelWithHint/LabelWithHint"
import { shortDate } from "../../../../../../Helpers/Functions/dateFormat"
import { routePaths } from "../../../../../../Routes"
import { JobStatusPill } from "../Common/JobStatusPill"
import { TagsViewEdit } from "../Common/TagsViewEdit"
import { ColumnsProps } from "./JobsListTable/JobsListTable"

export const jobColumns: (
    vals: ColumnsProps<APIJobModel>
) => TableColumn<APIJobModel>[] = ({
    orgUsername,
    appID,
    sort,
    onReview,
    activeLearningStatusFetch
}) => {
    const isMetaAIApp =
        hasValue(activeLearningStatusFetch) &&
        activeLearningStatusFetch.value !== "unused"
    const cols: TableColumn<APIJobModel>[] = [
        {
            renderHeader: () => "File name",
            render: ({ input }) => {
                const url =
                    (input as { documentUrl: string })?.documentUrl ||
                    (input as { image_url: string })?.image_url ||
                    (input as { document_url: string })?.document_url
                return url ? (
                    <LabelWithHint
                        label={
                            <Text
                                className="m-0 max-w-xs truncate"
                                weight="semibold"
                                size="sm"
                            >
                                {getFileName(url)}
                            </Text>
                        }
                        hint={
                            <Text className="m-0" size="sm">
                                {getFileName(url)}
                            </Text>
                        }
                    />
                ) : (
                    <Text className="m-0" weight="semibold" size="sm">
                        -
                    </Text>
                )
            },
            className: "w-2/12"
        },
        {
            renderHeader: () => "Data point",
            render: ({ id }) => (
                <Text className="m-0" weight="semibold" size="sm">
                    {id}
                </Text>
            ),
            className: "w-2/12"
        },
        {
            renderHeader: () => "Pages",
            render: ({ internalMetadata }) => (
                <Text className="m-0" weight="semibold" size="sm">
                    {internalMetadata?.num_pages
                        ? internalMetadata.num_pages
                        : "-"}
                </Text>
            ),
            className: "w-2/12"
        }
    ]

    if (isMetaAIApp) {
        cols.push({
            renderHeader: () => "Model confidence",
            render: ({ score }) => {
                const isCalculating =
                    getValue(activeLearningStatusFetch) === "active" &&
                    (score === null || score === -2)

                return (
                    <Text
                        className={`m-0 ${
                            isCalculating ? "text-smoke-610" : ""
                        }`}
                        size="sm"
                    >
                        {isCalculating ? (
                            "Calculating..."
                        ) : score === -3 || score === null ? (
                            <NoData />
                        ) : (
                            score.toFixed(2)
                        )}
                    </Text>
                )
            },
            className: "w-2/12"
        })
    }
    cols.push(
        {
            renderHeader: () => "Tags",
            render: ({ tags }) => {
                const linkFactory = (tag: string) =>
                    routePaths.appDetail(orgUsername, appID, {
                        filter: [
                            {
                                fieldName: "tags",
                                operator: MultipleValueFilterOperator.isOneOf,
                                value: [tag]
                            }
                        ],
                        sort
                    })

                return (
                    <TagsViewEdit
                        className="-my-2"
                        appID={appID}
                        value={tags ?? []}
                        linkFactory={linkFactory}
                    />
                )
            },
            className: "w-2/12 text-smoke-610"
        },
        {
            renderHeader: () => "Created",
            render: ({ created }) => shortDate(created),
            className: "w-2/12 text-smoke-610"
        },
        {
            renderHeader: () => "Status",
            render: (job, isChecked) => {
                const handleReview = () => onReview?.(job, isChecked)
                return (
                    <div className="flex items-center">
                        <JobStatusPill job={job} />
                        {job.status === JobStatus.COMPLETED &&
                            job.correct === null &&
                            onReview && (
                                <FlatButton
                                    className="-my-1 ml-8 whitespace-no-wrap"
                                    onClick={handleReview}
                                >
                                    Review
                                </FlatButton>
                            )}
                    </div>
                )
            },
            className: "w-2/12 min-w-max-content"
        }
    )

    return cols
}
